import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '@hero/schema';
import { Input } from '@hero/web-components';
import { OrderModal } from '@hero/web-components';
import { ReceiptIcon, FileTextIcon, LoadMainIcon } from '@hero/web-icons';

export interface PODRefCellModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order;
  onSave: (podRef: string) => Promise<void>;
}

/**
 * Modal for editing POD reference (My Ref)
 * Uses the reusable OrderModal component with route display, ref, and customer info
 * Includes explanation with icons about what this reference can be used for
 */
export const PODRefCellModal: React.FC<PODRefCellModalProps> = ({
  isOpen,
  onClose,
  order,
  onSave,
}) => {
  const { t } = useTranslation('dashboard');
  const [podRefValue, setPodRefValue] = useState(order.podRef || '');
  const [isSaving, setIsSaving] = useState(false);

  // Reset to original value when modal opens or order changes
  useEffect(() => {
    if (isOpen) {
      setPodRefValue(order.podRef || '');
    }
  }, [isOpen, order.podRef]);

  // Reset to original value when modal closes without saving
  const handleClose = () => {
    setPodRefValue(order.podRef || '');
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(podRefValue);
      onClose();
    } catch (error) {
      console.error('Failed to save POD reference:', error);
      // Error is handled by the parent component's mutation
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <OrderModal
      open={isOpen}
      onOpenChange={handleClose}
      order={order}
      title={t('table.columns.myRef')}
      size="md"
      preventOutsideClose={isSaving}
      actions={{
        cancel: {
          label: t('common.cancel'),
          onClick: handleClose,
          disabled: isSaving,
        },
        save: {
          label: t('common.save'),
          onClick: handleSave,
          disabled: isSaving,
          loading: isSaving,
        },
      }}>
      {/* Description */}
      <p className="text-sm text-muted-foreground mb-4">
        {t('table.columns.myRefDescription')}
      </p>

      {/* Explanation with icons */}
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600 mb-3">
          {t('table.columns.myRefExamples')}
        </p>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <ReceiptIcon className="w-5 h-5 text-gray-600" />
            <span className="text-sm text-gray-700">
              {t('table.columns.invoiceNumber')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <FileTextIcon className="w-5 h-5 text-gray-600" />
            <span className="text-sm text-gray-700">
              {t('table.columns.orderNumber')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LoadMainIcon className="w-5 h-5 text-gray-600" />
            <span className="text-sm text-gray-700">
              {t('table.columns.deliveryNote')}
            </span>
          </div>
        </div>
      </div>

      {/* Input field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('table.columns.myRef')}
        </label>
        <Input
          value={podRefValue}
          onChange={e => setPodRefValue(e.target.value)}
          placeholder={t('table.columns.myRefPlaceholder')}
          className="w-full"
        />
      </div>
    </OrderModal>
  );
};
